import * as React from 'react';
import classNames from 'classnames';

import Icon from 'components/icon';

import { wizardSteps, WizardStepId } from 'tempcontrol/enums';

export type IntroColumnVariant = 'intro' | 'step';

interface IntroColumnProps {
  currentStep: WizardStepId;
  onBack?: () => void;
  title: string;
  body?: string;
  /** Optional callout slot rendered below the body (e.g. info tooltip). */
  callout?: React.ReactNode;
  /**
   * `intro` matches s1 (no step counter or progress bar).
   * `step` matches s2–s4 (back link, progress, numbered step label).
   */
  variant?: IntroColumnVariant;
}

/**
 * Left column of the wizard body. Contains the BACK link, optional
 * progress bar, step indicator, heading + body copy, and the soft
 * beige circle decoration that bleeds across into the form column.
 */
export const IntroColumn: React.VFC<IntroColumnProps> = ({
  currentStep,
  onBack,
  title,
  body,
  callout,
  variant = 'step',
}) => {
  const totalSteps = wizardSteps.length;
  // Cutup progress: step 2 = 25%, 3 = 50%, 4 = 75% (completed segments, not current step).
  const progressPercent = Math.round(((currentStep - 1) / totalSteps) * 100);
  const isIntro = variant === 'intro';

  return (
    <>
      {typeof onBack === 'function' && (
        <button
          type="button"
          className="c-link-cta-ghost c-link-cta--small c-block__get-back u-mb-spacer-base-small"
          onClick={onBack}
        >
          <Icon name="arrow" className="o-svg-icon o-svg-left" />
          <span>Back</span>
        </button>
      )}

      {!isIntro && (
        <div className="c-metrics-goal c-metrics-goal--simple u-mb-spacer-base" aria-hidden="true">
          <div className="c-metrics-goal__bar">
            <div className="c-metrics-goal__progress" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      )}

      <div className="c-block__header">
        {/* Step counter shows on every step (including the s1 intro) so the
            first page stays consistent with "Step X / 3". The progress bar
            above stays hidden on the intro (it would read as an empty 0% track). */}
        <p className="c-headline u-mb-spacer-base-small" aria-label={`Step ${currentStep} of ${totalSteps}`}>
          Step{' '}
          <span className="u-text-xl u-pl-spacer">
            <span className="u-font-medium u-text-primary">{currentStep}</span>
            <span className="u-px-spacer">/</span>
            {totalSteps}
          </span>
        </p>

        <h2 className={classNames(isIntro ? 'c-title--large' : 'c-title', 'u-mb-spacer-base')}>{title}</h2>

        {typeof body === 'string' && body.length > 0 && <p className="u-mb-spacer-base">{body}</p>}
      </div>

      {typeof callout !== 'undefined' && callout}

      <div className="c-block__decoration-circle" aria-hidden="true" />
    </>
  );
};

export default IntroColumn;
